import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, Input, Spinner, Table, Td, Th } from '@/components/admin/ui';
import {
  DeviceLink,
  EmptyState,
  Pagination,
} from '@/components';
import {
  BroadcastJobStatusPanel,
  formatBroadcastJobSummary,
  jobStatusBadgeVariant,
} from '@/components/admin/BroadcastJobProgress';
import { PushDeeplinkField } from '@/components/admin/PushDeeplinkField';
import type { EngagementCampaign, EngagementSendsResponse } from '@/lib/admin-types';
import { formatAdminDateTime, formatInt } from '@/lib/format';
import type {
  CreatePushBroadcastRes,
  PushBroadcastJob,
  PushBroadcastJobsRes,
  PushBroadcastPreviewTarget,
} from '@/lib/push-broadcast-types';
import { pushDataFromDeeplink } from '@/lib/push-deeplinks';
import { apiFetch, apiJson, getApiErrorMessage } from '@/lib/api';

type TestPushRes = { success: boolean; message: string; tokens_sent: number };

type DictionaryBackfillStatusRes = {
  seed_file: string;
  seed_file_found: boolean;
  seed_entry_count: number;
  db_entry_count: number;
};

type DictionaryBackfillRes = {
  status: 'inserted' | 'skipped';
  message: string;
  seed_file: string;
  seed_entry_count: number;
  entries_inserted: number;
  senses_inserted: number;
  examples_inserted: number;
  reviews_migrated: number;
  legacy_review_table_dropped: boolean;
  existing_entry_count: number;
};

type DictionaryBackfillProgressEvent = {
  type: 'progress';
  phase: string;
  percent: number;
  message: string;
  seed_entry_count?: number;
  db_entry_count?: number;
  parsed_count?: number;
};

type DictionaryBackfillStreamEvent =
  | DictionaryBackfillProgressEvent
  | { type: 'result'; result: DictionaryBackfillRes }
  | { type: 'error'; message: string };

type BackfillRunState =
  | { kind: 'idle' }
  | { kind: 'running'; phase: string; percent: number; message: string }
  | { kind: 'success'; result: DictionaryBackfillRes }
  | { kind: 'error'; message: string };

async function runDictionaryBackfillStream(
  onEvent: (event: DictionaryBackfillStreamEvent) => void
): Promise<void> {
  const res = await apiFetch('/v1/admin/dictionary/backfill/stream', {
    method: 'POST',
    headers: { Accept: 'text/event-stream' },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const body = text ? JSON.parse(text) : null;
      if (typeof body?.detail === 'string') {
        detail = body.detail;
      }
    } catch {
      // keep raw text
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }

  if (!res.body) {
    throw new Error('Backfill stream returned no response body');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';
    for (const chunk of chunks) {
      const line = chunk
        .split('\n')
        .find((part) => part.startsWith('data: '));
      if (!line) continue;
      onEvent(JSON.parse(line.slice(6)) as DictionaryBackfillStreamEvent);
    }
  }
}

function DictionaryBackfillProgressBar({
  percent,
  message,
  running,
}: {
  percent: number;
  message: string;
  running: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-nova-muted-foreground">{message}</span>
        <span className="font-medium tabular-nums text-nova-foreground">{clamped}%</span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-nova-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-busy={running}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${
            running ? 'bg-nova-primary' : 'bg-emerald-500'
          }`}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

function MutationResult({ error, message }: { error?: Error | null; message?: string }) {
  if (!error && !message) return null;
  return (
    <p className={`text-sm ${error ? 'text-red-600' : 'text-nova-muted-foreground'}`}>
      {error?.message ?? message}
    </p>
  );
}

function TestPushForm() {
  const [testId, setTestId] = useState('');
  const [testToken, setTestToken] = useState('');
  const [testTitle, setTestTitle] = useState('Test');
  const [testBody, setTestBody] = useState('Tap to open');
  const [testDeeplink, setTestDeeplink] = useState('');

  const testPush = useMutation({
    mutationFn: () => {
      const data = pushDataFromDeeplink(testDeeplink);
      const body: Record<string, unknown> = { title: testTitle, body: testBody };
      if (testId.trim()) body.installation_id = testId.trim();
      if (testToken.trim()) body.expo_push_token = testToken.trim();
      if (data) body.data = data;
      return apiJson<TestPushRes>('/v1/admin/send-test-push', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  });

  const resultMessage = testPush.data
    ? `${testPush.data.success ? 'OK' : 'Not OK'}: ${testPush.data.message} (sent ${formatInt(testPush.data.tokens_sent)})`
    : undefined;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-nova-foreground">Test to one device</h3>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Send to an installation ID or raw Expo token. Optionally choose a deeplink target for
          when the user taps the notification.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="installation_id"
          value={testId}
          onChange={(e) => setTestId(e.target.value)}
          placeholder="UUID"
        />
        <Input
          label="expo_push_token"
          value={testToken}
          onChange={(e) => setTestToken(e.target.value)}
          placeholder="ExponentPushToken[...]"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={testTitle} onChange={(e) => setTestTitle(e.target.value)} />
        <Input label="Body" value={testBody} onChange={(e) => setTestBody(e.target.value)} />
      </div>

      <PushDeeplinkField value={testDeeplink} onChange={setTestDeeplink} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <MutationResult error={testPush.error} message={resultMessage} />
        <Button
          className="sm:ml-auto"
          onClick={() => {
            if (!testId.trim() && !testToken.trim()) {
              alert('Set installation_id and/or expo_push_token');
              return;
            }
            void testPush.mutate();
          }}
          disabled={!testId.trim() && !testToken.trim()}
          loading={testPush.isPending}
        >
          Send test push
        </Button>
      </div>
    </div>
  );
}

function BroadcastPreviewTable({ targets, totalTargets }: { targets: PushBroadcastPreviewTarget[]; totalTargets: number }) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-nova-muted-foreground">
        Previewing {formatInt(Math.min(targets.length, totalTargets))} of {formatInt(totalTargets)} targets.
      </p>
      <div className="max-w-full overflow-x-auto">
        <Table>
          <thead>
            <tr>
              <Th>Device</Th>
              <Th>Platform</Th>
              <Th>Token</Th>
            </tr>
          </thead>
          <tbody>
            {targets.map((target) => (
              <tr key={target.installation_id}>
                <Td>
                  <DeviceLink
                    installationId={target.installation_id}
                    nickname={target.nickname}
                  />
                </Td>
                <Td className="text-sm">{target.platform}</Td>
                <Td className="max-w-[16rem] truncate font-mono text-xs text-nova-muted-foreground">
                  {target.expo_push_token}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function BroadcastForm() {
  const [bcTitle, setBcTitle] = useState('Hmonglus');
  const [bcBody, setBcBody] = useState('Tap to open the app');
  const [bcDeeplink, setBcDeeplink] = useState('');
  const [bcIds, setBcIds] = useState('');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const broadcast = useMutation({
    mutationFn: (dryRun: boolean) => {
      const data = pushDataFromDeeplink(bcDeeplink);
      const body: Record<string, unknown> = {
        title: bcTitle,
        body: bcBody,
        dry_run: dryRun,
      };
      if (bcIds.trim()) {
        body.installation_ids = bcIds.split(/[\s,]+/).filter(Boolean);
      }
      if (data) body.data = data;
      return apiJson<CreatePushBroadcastRes>('/v1/admin/push-broadcasts', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (res) => {
      setActiveJobId(res.job.id);
    },
  });

  const jobQ = useQuery({
    queryKey: ['admin', 'push-broadcast', activeJobId],
    queryFn: () => apiJson<PushBroadcastJob>(`/v1/admin/push-broadcasts/${activeJobId}`),
    enabled: Boolean(activeJobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || status === 'completed' || status === 'failed') {
        return false;
      }
      return 2000;
    },
  });

  const activeJob = jobQ.data ?? null;
  const isRunning =
    Boolean(activeJobId) &&
    (!activeJob || activeJob.status === 'pending' || activeJob.status === 'running');

  const startBroadcast = (dryRun: boolean) => {
    const listedCount = bcIds.split(/[\s,]+/).filter(Boolean).length;
    const action = dryRun ? 'Run dry run' : 'Send broadcast';
    const audience = bcIds.trim()
      ? `${formatInt(listedCount)} listed installation IDs`
      : 'ALL push-ready iOS/Android users';
    const warning = dryRun
      ? `Resolve the audience for ${audience} without sending any pushes?`
      : `Send a real broadcast to ${audience}? This cannot be undone.`;
    if (!confirm(`${action}: ${warning}`)) {
      return;
    }
    void broadcast.mutate(dryRun);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-nova-foreground">Broadcast</h3>
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 py-2 px-2.5 text-sm text-amber-900">
          Broadcasts run as background jobs. Start with a dry run to verify the audience, title, and
          body before sending for real.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={bcTitle} onChange={(e) => setBcTitle(e.target.value)} />
        <Input label="Body" value={bcBody} onChange={(e) => setBcBody(e.target.value)} />
      </div>

      <PushDeeplinkField value={bcDeeplink} onChange={setBcDeeplink} />

      <div>
        <label className="mb-1 block text-sm text-nova-muted-foreground">
          installation_ids <span className="text-nova-muted-foreground">(optional, comma or space separated)</span>
        </label>
        <textarea
          className="w-full min-h-[56px] rounded-lg border border-nova-border py-2 px-2.5 text-sm"
          value={bcIds}
          onChange={(e) => setBcIds(e.target.value)}
          placeholder="Leave empty to send to all push-ready devices"
        />
      </div>

      {broadcast.error ? (
        <p className="text-sm text-red-600">{broadcast.error.message}</p>
      ) : null}

      {activeJob ? (
        <div className="space-y-3">
          <BroadcastJobStatusPanel job={activeJob} />
          {activeJob.status === 'completed' && activeJob.preview_targets?.length ? (
            <BroadcastPreviewTable
              targets={activeJob.preview_targets}
              totalTargets={activeJob.total_targets}
            />
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        <Button
          variant="secondary"
          onClick={() => startBroadcast(true)}
          loading={broadcast.isPending || (isRunning && activeJob?.dry_run === true)}
          disabled={isRunning && activeJob?.dry_run !== true}
        >
          Run dry run
        </Button>
        <Button
          variant="danger"
          onClick={() => startBroadcast(false)}
          loading={broadcast.isPending || (isRunning && activeJob?.dry_run === false)}
          disabled={isRunning && activeJob?.dry_run !== false}
        >
          Send broadcast
        </Button>
      </div>
    </div>
  );
}

function BroadcastJobsPanel() {
  const jobsQ = useQuery({
    queryKey: ['admin', 'push-broadcasts'],
    queryFn: () => apiJson<PushBroadcastJobsRes>('/v1/admin/push-broadcasts?limit=20'),
    refetchInterval: 5000,
  });

  return (
    <Card padding="md">
      {jobsQ.isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {jobsQ.isError && (
        <p className="text-sm text-red-600">{(jobsQ.error as Error).message}</p>
      )}
      {jobsQ.data && jobsQ.data.items.length === 0 && (
        <EmptyState title="No broadcast jobs yet" />
      )}
      {jobsQ.data && jobsQ.data.items.length > 0 && (
        <div className="max-w-full overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Status</Th>
                <Th>Title</Th>
                <Th>Targets</Th>
                <Th>Summary</Th>
              </tr>
            </thead>
            <tbody>
              {jobsQ.data.items.map((job) => (
                <tr key={job.id}>
                  <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                    {formatAdminDateTime(job.created_at)}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={jobStatusBadgeVariant(job.status)}>{job.status}</Badge>
                      {job.dry_run ? <Badge variant="warning">dry run</Badge> : null}
                    </div>
                  </Td>
                  <Td className="max-w-[12rem] truncate text-sm">
                    {job.title}
                    <span className="block truncate text-xs text-nova-muted-foreground">{job.body}</span>
                  </Td>
                  <Td className="text-sm tabular-nums">{formatInt(job.total_targets)}</Td>
                  <Td className="max-w-[16rem] text-sm text-nova-muted-foreground">
                    {formatBroadcastJobSummary(job)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function PushNotificationsSection() {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-nova-foreground">Push notifications</h2>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Test a single device, dry-run a broadcast audience, or send to the mobile audience.
        </p>
      </div>
      <Card padding="lg" className="space-y-8">
        <TestPushForm />
        <div className="border-t border-nova-border pt-8">
          <BroadcastForm />
        </div>
      </Card>
    </section>
  );
}

function DictionaryBackfillSection() {
  const [runState, setRunState] = useState<BackfillRunState>({ kind: 'idle' });

  const statusQ = useQuery({
    queryKey: ['admin', 'dictionary', 'backfill-status'],
    queryFn: () => apiJson<DictionaryBackfillStatusRes>('/v1/admin/dictionary/backfill/status'),
    refetchInterval: runState.kind === 'running' ? 2000 : false,
  });

  const backfill = useMutation({
    mutationFn: async () => {
      setRunState({
        kind: 'running',
        phase: 'starting',
        percent: 0,
        message: 'Starting dictionary backfill…',
      });

      let finalResult: DictionaryBackfillRes | null = null;
      let streamError: string | null = null;

      await runDictionaryBackfillStream((event) => {
        if (event.type === 'progress') {
          setRunState({
            kind: 'running',
            phase: event.phase,
            percent: event.percent,
            message: event.message,
          });
          return;
        }
        if (event.type === 'result') {
          finalResult = event.result;
          setRunState({ kind: 'success', result: event.result });
          return;
        }
        streamError = event.message;
        setRunState({ kind: 'error', message: event.message });
      });

      if (streamError) {
        throw new Error(streamError);
      }
      if (!finalResult) {
        throw new Error('Backfill finished without a result');
      }
      return finalResult;
    },
    onSuccess: () => {
      void statusQ.refetch();
    },
    onError: (error: unknown) => {
      setRunState({
        kind: 'error',
        message: getApiErrorMessage(error, 'Dictionary backfill failed'),
      });
    },
  });

  const status = statusQ.data;
  const seedCount = status?.seed_entry_count;
  const dbCount = status?.db_entry_count;
  const countsInSync =
    status?.seed_file_found &&
    seedCount !== undefined &&
    dbCount !== undefined &&
    seedCount > 0 &&
    seedCount === dbCount;
  const needsBackfill =
    status?.seed_file_found && dbCount === 0 && (seedCount ?? 0) > 0;
  const isRunning = runState.kind === 'running' || backfill.isPending;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-nova-foreground">Dictionary seed backfill</h2>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Load bundled JSONL seed data into Postgres after deploy. Safe to run once; skips if entries
          already exist.
        </p>
      </div>
      <Card padding="lg" className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-nova-border bg-nova-muted p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-nova-muted-foreground">Seed file terms</p>
            <div className="mt-2 flex items-center gap-2">
              {statusQ.isLoading ? (
                <Spinner />
              ) : statusQ.isError ? (
                <span className="text-sm text-red-600">Could not load</span>
              ) : !status?.seed_file_found ? (
                <Badge variant="danger">Missing</Badge>
              ) : (
                <span className="text-xl font-semibold tracking-tight tabular-nums text-nova-foreground">
                  {formatInt(seedCount ?? 0)}
                </span>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-nova-border bg-nova-muted p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-nova-muted-foreground">Database terms</p>
            <div className="mt-2 flex items-center gap-2">
              {statusQ.isLoading ? (
                <Spinner />
              ) : statusQ.isError ? (
                <span className="text-sm text-red-600">Could not load</span>
              ) : (
                <>
                  <span className="text-xl font-semibold tracking-tight tabular-nums text-nova-foreground">
                    {formatInt(dbCount ?? 0)}
                  </span>
                  {countsInSync ? (
                    <Badge variant="success">In sync</Badge>
                  ) : needsBackfill ? (
                    <Badge variant="warning">Needs backfill</Badge>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {status?.seed_file ? (
          <p className="font-mono text-xs text-nova-muted-foreground break-all">{status.seed_file}</p>
        ) : null}

        {statusQ.isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 py-2 px-2.5 text-sm text-red-700">
            Could not load backfill status: {(statusQ.error as Error).message}
          </div>
        ) : null}

        {isRunning || runState.kind === 'success' || runState.kind === 'error' ? (
          <div className="space-y-3 rounded-lg border border-nova-border bg-nova-card py-2 px-2.5">
            <p className="text-sm font-medium text-nova-foreground">Progress</p>
            {runState.kind === 'running' ? (
              <DictionaryBackfillProgressBar
                percent={runState.percent}
                message={runState.message}
                running
              />
            ) : runState.kind === 'success' ? (
              <DictionaryBackfillProgressBar
                percent={100}
                message={runState.result.message}
                running={false}
              />
            ) : runState.kind === 'error' ? (
              <DictionaryBackfillProgressBar percent={0} message="Backfill failed" running={false} />
            ) : (
              <DictionaryBackfillProgressBar
                percent={0}
                message="Starting dictionary backfill…"
                running
              />
            )}
          </div>
        ) : null}

        {runState.kind === 'success' ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 py-2 px-2.5 text-sm text-emerald-900">
            <p className="font-medium capitalize">{runState.result.status}</p>
            <p className="mt-1">{runState.result.message}</p>
            <ul className="mt-2 space-y-1 text-emerald-800">
              <li>Seed file terms: {formatInt(runState.result.seed_entry_count)}</li>
              <li>
                Inserted: {formatInt(runState.result.entries_inserted)} entries,{' '}
                {formatInt(runState.result.senses_inserted)} senses,{' '}
                {formatInt(runState.result.examples_inserted)} examples
              </li>
              {runState.result.reviews_migrated > 0 ? (
                <li>Legacy reviews migrated: {formatInt(runState.result.reviews_migrated)}</li>
              ) : null}
              {runState.result.legacy_review_table_dropped ? (
                <li>Legacy review table dropped</li>
              ) : null}
            </ul>
          </div>
        ) : null}

        {runState.kind === 'error' ? (
          <div className="rounded-lg border border-red-200 bg-red-50 py-2 px-2.5 text-sm text-red-700">
            <p className="font-medium">Error</p>
            <p className="mt-1">{runState.message}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-nova-muted-foreground">
            {needsBackfill
              ? 'Database is empty; run backfill to import seed terms.'
              : countsInSync
                ? 'Seed file and database term counts match.'
                : 'Backfill is idempotent when the database already has entries.'}
          </p>
          <Button
            className="sm:ml-auto"
            onClick={() => {
              if (
                !confirm(
                  (dbCount ?? 0) > 0
                    ? 'Dictionary already has entries. Backfill will skip (idempotent). Run anyway?'
                    : 'Import dictionary seed from bundled JSONL into Postgres?'
                )
              ) {
                return;
              }
              void backfill.mutate();
            }}
            loading={isRunning}
            disabled={statusQ.isLoading || !status?.seed_file_found}
          >
            Run dictionary backfill
          </Button>
        </div>
      </Card>
    </section>
  );
}

function HistoryPanel() {
  const [campaign, setCampaign] = useState('');
  const [pushKey, setPushKey] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const campsQ = useQuery({
    queryKey: ['admin', 'engagement', 'campaigns'],
    queryFn: () => apiJson<EngagementCampaign[]>('/v1/admin/engagement/campaigns'),
  });

  const sendsQ = useQuery({
    queryKey: ['admin', 'engagement', 'sends', campaign, pushKey, q, page, pageSize],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('page', String(page));
      p.set('page_size', String(pageSize));
      if (campaign) p.set('campaign', campaign);
      if (pushKey) p.set('push_key', pushKey);
      if (q.trim()) p.set('q', q.trim());
      return apiJson<EngagementSendsResponse>(`/v1/admin/engagement/sends?${p.toString()}`);
    },
  });

  const campaignOptions = campsQ.data ?? [];
  const pushKeyOptions = campaign
    ? campaignOptions.filter((c) => c.campaign_name === campaign)
    : campaignOptions;

  return (
    <Card padding="md">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="min-w-0 flex-1">
          <Input
            label="Search (nickname or installation id)"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Filter sends"
          />
        </div>
        <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-auto lg:min-w-[14rem]">
          <div>
            <label className="mb-1 block text-sm text-nova-muted-foreground">Campaign</label>
            <select
              className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
              value={campaign}
              onChange={(e) => {
                setCampaign(e.target.value);
                setPushKey('');
                setPage(1);
              }}
            >
              <option value="">Any</option>
              {Array.from(new Set(campaignOptions.map((c) => c.campaign_name))).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm text-nova-muted-foreground">Push key</label>
            <select
              className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
              value={pushKey}
              onChange={(e) => {
                setPushKey(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Any</option>
              {Array.from(new Set(pushKeyOptions.map((c) => c.push_key))).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {sendsQ.isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {sendsQ.isError && (
        <p className="text-sm text-red-600">
          {(sendsQ.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void sendsQ.refetch()}>
            Retry
          </button>
        </p>
      )}
      {sendsQ.data && sendsQ.data.items.length === 0 && <EmptyState title="No sends match" />}
      {sendsQ.data && sendsQ.data.items.length > 0 && (
        <>
          <div className="max-w-full overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Campaign / key</Th>
                  <Th>Title</Th>
                  <Th>Device</Th>
                </tr>
              </thead>
              <tbody>
                {sendsQ.data.items.map((row) => (
                  <tr key={row.id}>
                    <Td className="whitespace-nowrap text-xs text-nova-muted-foreground">
                      {formatAdminDateTime(row.sent_at)}
                    </Td>
                    <Td>
                      <Badge variant="primary">
                        {row.campaign_name}/{row.push_key}
                      </Badge>
                    </Td>
                    <Td className="max-w-[12rem] truncate text-sm">
                      {row.title ?? '—'}
                      {row.body ? (
                        <span className="block truncate text-xs text-nova-muted-foreground">{row.body}</span>
                      ) : null}
                    </Td>
                    <Td>
                      <DeviceLink
                        installationId={row.installation_id}
                        nickname={row.nickname}
                      />
                      {row.platform ? (
                        <span className="ml-1 text-xs text-nova-muted-foreground">({row.platform})</span>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination
            page={sendsQ.data.page}
            pageSize={sendsQ.data.page_size}
            total={sendsQ.data.total}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}

function EngagementPage() {
  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Tools</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Push notifications and other admin utilities for outreach and debugging.
        </p>
      </div>

      <PushNotificationsSection />

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-nova-foreground">Broadcast jobs</h2>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Recent admin broadcast and dry-run jobs with live status.
          </p>
        </div>
        <BroadcastJobsPanel />
      </section>

      <DictionaryBackfillSection />

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-nova-foreground">Send history</h2>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Automated campaign sends and delivery log across devices.
          </p>
        </div>
        <HistoryPanel />
      </section>
    </div>
  );
}

export const Route = createFileRoute('/admin/engagement')({
  component: EngagementPage,
});
